import { useContext } from 'react'
import { useArcTranslation } from 'hooks'
import { Stack, Group, Text, SimpleGrid, Skeleton, EmptyStateBox, Button, Carousel, carouselDashboard } from 'components'
import { IconUsersGroup, IconArticleOff, IconUsersPlus, IconUsers } from '@tabler/icons-react'
import { SimpleGridBreakpoint, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from 'hooks'
import { useNavigate } from 'react-router-dom'
import { TeacherDashboardContext } from '../../context'
import { ArcGroupCard } from '../ArcGroupCard'
import { useAddStudentsModal } from '../AddStudentsModal'

const CAROUSEL_THRESHOLD = 3

const GRID_BREAKPOINTS: SimpleGridBreakpoint[] = [
  { maxWidth: 'md', cols: 2 },
  { maxWidth: 'sm', cols: 1 },
]

export const ArcGroupsSection = () => {
  const { t } = useArcTranslation()
  const navigate = useNavigate()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const { groups } = useContext(TeacherDashboardContext)
  const { open: openAddStudentsModal } = useAddStudentsModal()

  const isLoading = groups === null
  const hasGroups = groups && groups.length > 0
  const useCarousel = hasGroups && groups.length > CAROUSEL_THRESHOLD
  const membersLabel = t('new_teacher_dashboard.group_card_students')

  const renderContent = () => {
    if (isLoading) {
      return (
        <SimpleGrid cols={3} spacing='md' breakpoints={GRID_BREAKPOINTS}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} height={200} radius='md' animate />
          ))}
        </SimpleGrid>
      )
    }

    if (!hasGroups) {
      return (
        <EmptyStateBox
          withBorder
          text={t('new_teacher_dashboard.no_groups')}
          icon={<IconArticleOff />}
          actionButton={
            <Button
              variant='filled'
              color='teal'
              radius='xl'
              leftIcon={<IconUsers size={18} />}
              onClick={() => navigate('/teacher/manage-students')}
            >
              {t('manage_students.dashboard_button')}
            </Button>
          }
        />
      )
    }

    if (useCarousel) {
      return (
        <Carousel {...carouselDashboard}>
          {groups.map(group => (
            <Carousel.Slide key={group.id}>
              <ArcGroupCard group={group} membersLabel={membersLabel} />
            </Carousel.Slide>
          ))}
        </Carousel>
      )
    }

    return (
      <SimpleGrid cols={3} spacing='md' breakpoints={GRID_BREAKPOINTS}>
        {groups.map(group => (
          <ArcGroupCard key={group.id} group={group} membersLabel={membersLabel} />
        ))}
      </SimpleGrid>
    )
  }

  return (
    <Stack spacing='md'>
      <Group position='apart'>
        <Group spacing='xs'>
          <IconUsersGroup size={24} />
          <Text size='lg' weight={700}>
            {t('new_teacher_dashboard.groups_title')}
          </Text>
        </Group>
        {isMobile && (
          <Button
            variant='filled'
            color='teal'
            radius='xl'
            size='sm'
            leftIcon={<IconUsersPlus size={16} />}
            onClick={() => openAddStudentsModal()}
          >
            {t('add_students_modal.title')}
          </Button>
        )}
      </Group>
      {renderContent()}
    </Stack>
  )
}
